import React, { useEffect, useState } from 'react';
import { Image } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '@/config/api';

interface PuppeteerInstance {
  sessionId: string;
  status: 'active' | 'idle';
  lastAction: string;
  lastScreenshot?: string;
}

export default function PuppeteerGallery() {
  const [instances, setInstances] = useState<PuppeteerInstance[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/puppeteer/status`);
      setInstances(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Puppeteer status:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Image className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Puppeteer Gallery</h1>
        </div>
        <p className="text-muted-foreground">View and monitor active Puppeteer browser instances</p>
      </div>
      <div className="flex-1 bg-background rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4">Puppeteer Browser Instances</h2>
        
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Loading...</div>
        ) : instances.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">No active Puppeteer instances</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {instances.map((instance) => (
              <div key={instance.sessionId} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-center mb-3">
                  <div className="font-medium text-sm truncate" title={instance.sessionId}>
                    {instance.sessionId}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full uppercase font-medium ${
                    instance.status === 'active' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' 
                      : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100'
                  }`}>
                    {instance.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mb-3">
                  Last action: {new Date(instance.lastAction).toLocaleString()}
                </div>
                {instance.lastScreenshot ? (
                  <img 
                    src={instance.lastScreenshot} 
                    alt={`Screenshot for session ${instance.sessionId}`}
                    className="w-full h-auto rounded border"
                  />
                ) : (
                  <div className="w-full h-40 bg-muted rounded flex items-center justify-center text-muted-foreground text-sm">
                    No screenshot available
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}